import { useQuery } from '@tanstack/react-query'
import { Message } from '~/components/chat/message'
import { getTaskRequest } from '~/contexts/TaskContext'
import { useWorkspaceContext } from '~/contexts/WorkspaceContext'
import { QueryKeys } from '~/services/queryClient'
import { TaskObject } from '~/types/TaskType'
import { fetchWrapper } from '~/utils/fetchWrapper'

export interface TaskDetails {
  task: TaskObject
  messages: Message[]
  /** Whether the task result is visible (false if credit limit exceeded when task was executed) */
  is_result_visible?: boolean
}

const fetchTaskDetails = async (
  workspaceUuid: string,
  conversationUuid: string,
  taskId: string
): Promise<TaskDetails> => {
  const response = await fetchWrapper(
    `/api/workspace/${workspaceUuid}/conversation/${conversationUuid}/task/${taskId}`,
    {
      method: 'GET'
    }
  )

  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to fetch task details: ${reason}`)
  }

  return response.json()
}

export const useTaskDetails = (
  conversationUuid: string,
  taskId: string,
  options?: {
    refetchInterval?: number | false
  }
) => {
  const { currentWorkspace } = useWorkspaceContext()
  const workspaceUuid = currentWorkspace?.uuid || ''

  return useQuery({
    queryKey: QueryKeys.TaskDetails(conversationUuid, taskId),
    queryFn: () => fetchTaskDetails(workspaceUuid, conversationUuid!, taskId!),
    enabled: !!workspaceUuid && !!conversationUuid && !!taskId,
    refetchInterval: options?.refetchInterval
  })
}

// Helper to extract essential form data from original request
export const extractFormDataFromTask = (taskDetails: TaskDetails): Record<string, unknown> => {
  const originalRequest = getTaskRequest(taskDetails.task)

  if (!originalRequest) {
    return {}
  }

  return {
    // Core command data
    commandString: originalRequest.command?.commandString || '',

    // Repository context
    repository: originalRequest.metadata?.codeContext?.repoName || '',
    branch: originalRequest.metadata?.codeContext?.branchName || '',
    codeContext: originalRequest.metadata?.codeContext,
    multiRepoCodeContext: originalRequest.metadata?.multiRepoCodeContext || [],

    // Runtime settings
    runtimeSettings: originalRequest.runtimeSettings,
    vmArgs: originalRequest.vmArgs || '',

    // Common form fields
    sourceOutputExamples: originalRequest.sourceOutputExamples || [],
    mappingSheet: originalRequest.mappingSheet || [],
    form: originalRequest.form || [],
    userNotes: originalRequest.userNotes || '',

    // Feature flags
    runTests: originalRequest.runTests || false,
    enableWebSearch: originalRequest.enableWebSearch || false,

    // Command-specific data (only include if present)
    ...(originalRequest.command?.images && { images: originalRequest.command.images }),
    ...(originalRequest.selectedEndpoints && {
      selectedEndpoints: originalRequest.selectedEndpoints
    }),
    ...(originalRequest.userSelectedEntities && {
      userSelectedEntities: originalRequest.userSelectedEntities
    })
  }
}
